/* eslint-disable indent */
import { CheckPoint } from '@/api/entities/check-point';
import { CheckpointGroup } from '@/api/entities/checkpointGroup';
import { Equipment } from '@/api/entities/equipment';
import { Accordion } from '@/components/Accordion';
import Card from '@/components/Card';
import { Checkbox } from '@/components/Controls/Checkbox';
import { NotFound } from '@/components/Message/NotFound';
import {
  useCreateCheckPoinGroup,
  useUpdateCheckpointGroup,
} from '@/hooks/checkpoint-group.hooks';
import { TFunction } from 'i18next';
import { useEffect, useMemo, useState } from 'react';

interface GroupCheckPointEquipmentsProps {
  t: TFunction<undefined>;
  groupId: string;
  checkPoints: CheckPoint[];
  equipments: Equipment[];
  checkpointGroup: CheckpointGroup[];
}

export const GroupCheckPointEquipments = ({
  t,
  groupId,
  checkPoints,
  equipments,
  checkpointGroup,
}: GroupCheckPointEquipmentsProps) => {
  const [checkboxValues, setCheckboxValues] =
    useState<Record<string, boolean>>();

  const isLoading = useMemo(
    () => !checkPoints && !equipments && !checkpointGroup,
    [checkPoints, checkpointGroup, equipments],
  );

  useEffect(() => {
    setCheckboxValues(() =>
      checkPoints
        ? checkPoints.reduce(
            (acc, checkPoint) => {
              const relevantCheckpointGroup = checkpointGroup?.find(
                (checkpoint) => checkpoint.checkpointId === checkPoint.id,
              );

              relevantCheckpointGroup?.equipmentIds?.forEach((equipmentId) => {
                acc[`${checkPoint.id}-${equipmentId}`] = true; // Mark as checked
              });

              return acc;
            },
            {} as Record<string, boolean>,
          )
        : {},
    );
  }, [checkPoints, checkpointGroup]);

  const { mutate: updateEquipment } = useUpdateCheckpointGroup();
  const { mutate: createCheckpointGroup } = useCreateCheckPoinGroup();

  const handleCheckboxChange = (
    equipment: Equipment,
    checkPointId: string,
    isChecked: boolean,
  ) => {
    setCheckboxValues((prev) => ({
      ...prev,
      [`${checkPointId}-${equipment?.id}`]: isChecked,
    }));

    const relevantCheckpointGroup = checkpointGroup?.find(
      (group) => group.checkpointId === checkPointId,
    );

    const updatedEquipmentIds = isChecked
      ? [...(relevantCheckpointGroup?.equipmentIds || []), equipment.id]
      : relevantCheckpointGroup?.equipmentIds?.filter(
          (id) => id !== equipment.id,
        ) || [];

    const dataMapped: CheckpointGroup = {
      ...relevantCheckpointGroup,
      groupId: groupId,
      checkpointId: checkPointId,
      equipmentIds: updatedEquipmentIds,
    };

    if (dataMapped?.id) {
      updateEquipment(dataMapped);
    } else {
      createCheckpointGroup(dataMapped);
    }
  };

  return (
    <Card
      title={
        <div className="flex gap-2">
          <i className="ki-filled ki-notepad-bookmark text-purple-500 text-[25px]" />
          <p>{t('equipmentsPerCheckPoint')}</p>
        </div>
      }
    >
      {!isLoading && !checkPoints?.length ? (
        <div className="mt-7 mb-6">
          <NotFound text={`${t('groupCheckPointNotFound')}...`} />
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {checkPoints?.map((item, index) => (
            <Accordion
              key={`${item?.id}-${index}`}
              title={`${item?.name} (${item?.type})`}
              titleClassName="uppercase"
            >
              <>
                <div className="flex bg-purple-9D8AFF/[9.5%] px-5 pt-2.75 pb-2 rounded-lg gap-2 mb-5 max-sm:px-3">
                  <i className="-mt-[5px] ki-filled ki-information-2 text-purple-A713F6 text-xl" />
                  <div className="text-sm text-purple-A713F6 font-semibold">
                    {t('selectEquipmentsToNotList')}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 max-h-[400px] pb-5 overflow-y-auto max-1.5xl:grid-cols-2 max-sm:grid-cols-1">
                  {equipments?.map((equipment) => (
                    <Checkbox
                      key={`${item?.id}-${equipment?.id}`}
                      text={equipment?.name}
                      checked={checkboxValues?.[`${item?.id}-${equipment?.id}`]}
                      onChange={(e) =>
                        handleCheckboxChange(
                          equipment,
                          item?.id,
                          e.target.checked,
                        )
                      }
                    />
                  ))}
                </div>
              </>
            </Accordion>
          ))}
        </div>
      )}
    </Card>
  );
};
